import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Scripts,
	useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { useEffect } from "react";
import BackToTop from "../components/BackToTop";

import { ConsentManager } from "../components/consent-manager";
import { DefaultError, NotFound } from "../components/error-pages";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { LangProvider } from "../contexts/lang";
import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";
import appCss from "../styles.css?url";

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: "Македонијатурист АД",
			},
			{
				name: "description",
				content:
					"Makedonijaturist AD, корпоративен бренд и портфолио на хотели во Скопје. Локално срце. Градски комфорт.",
			},
			{
				property: "og:title",
				content: "Македонијатурист АД",
			},
			{
				property: "og:description",
				content:
					"Makedonijaturist AD, корпоративен бренд и портфолио на хотели во Скопје. Локално срце. Градски комфорт.",
			},
			{
				property: "og:image",
				content: "https://makedonijaturist.com.mk/makedonijaturist.png",
			},
			{
				property: "og:type",
				content: "website",
			},
			{
				name: "twitter:card",
				content: "summary_large_image",
			},
			{
				name: "twitter:title",
				content: "Македонијатурист АД",
			},
			{
				name: "twitter:description",
				content:
					"Makedonijaturist AD, корпоративен бренд и портфолио на хотели во Скопје. Локално срце. Градски комфорт.",
			},
			{
				name: "twitter:image",
				content: "https://makedonijaturist.com.mk/makedonijaturist.png",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk",
			},
			{
				rel: "stylesheet",
				href: appCss,
			},
		],
	}),
	errorComponent: DefaultError,
	notFoundComponent: NotFound,
	shellComponent: RootDocument,
});

function ScrollToTop() {
	const location = useLocation();

	useEffect(() => {
		if (!location.hash) {
			window.scrollTo(0, 0);
		}
	}, [location.hash]);

	return null;
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { pathname } = useLocation();
	const isEn = pathname.startsWith("/en");
	const lang = isEn ? "en" : "mk";

	return (
		<html lang={lang} className="hyphens-auto">
			<head>
				<HeadContent />
			</head>
			<body>
				<LangProvider lang={lang}>
					<ConsentManager>
						<ScrollToTop />
						<Header />
						{children}
						<Footer />
						<BackToTop />
						<TanStackDevtools
							config={{
								position: "bottom-right",
							}}
							plugins={[
								{
									name: "Tanstack Router",
									render: <TanStackRouterDevtoolsPanel />,
								},
								TanStackQueryDevtools,
							]}
						/>
					</ConsentManager>
				</LangProvider>
				<Scripts />
			</body>
		</html>
	);
}
